import React, { useContext, useMemo } from 'react';
import { css, ThemeProvider } from 'styled-components';
import merge from 'lodash.merge';

var DEFAULT_PALETTE = {
  red: {
    50: '#fee7e7',
    100: '#fcd0ce',
    200: '#fbb8b6',
    300: '#f9a19e',
    400: '#f88985',
    500: '#f6726d',
    600: '#F55A55',
    700: '#F22B24',
    800: '#D1251F',
    900: '#880D0A'
  },
  green: {
    50: '#D5F3EB',
    100: '#ABE6D8',
    200: '#82DAC4',
    300: '#58CEB0',
    400: '#2EC19D',
    500: '#04B589',
    600: '#039772',
    700: '#03795B',
    800: '#03795B',
    900: '#013C2E'
  },
  blue: {
    50: '#EBEFFD',
    100: '#D6E0FB',
    200: '#C2D0F9',
    300: '#99B1F4',
    400: '#7091F0',
    500: '#4772EC',
    600: '#3F65D2',
    700: '#3759B8',
    800: '#2F4C9D',
    900: '#273F83'
  },
  gray: {
    50: '#F8F9FB',
    100: '#EFF2F6',
    200: '#E7E9ED',
    300: '#D3D6DC',
    400: '#C4C8D1',
    500: '#AFB4BF',
    600: '#9A9FAD',
    700: '#6F7588',
    800: '#303751',
    900: '#1A223E'
  },
  orange: {
    50: '#FDF5E3',
    100: '#FCEAC6',
    200: '#FAE0AA',
    300: '#F8D68E',
    400: '#F7CB71',
    500: '#F5C155',
    600: '#CCA147',
    700: '#A38139',
    800: '#7B612B',
    900: '#52401C'
  },
  transparent: 'transparent',
  black: '#2E2E2E',
  white: '#ffffff',
  brand: {
    primary: '#4772EC',
    hover: '#3759B8',
    active: '#3759B8',
    disabled: '#99B1F4'
  }
};

var DEFAULT_FONTS = {
  text: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  heading: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
};

var DEFAULT_FONT_SIZES = {
  xs: '12px',
  sm: '14px',
  md: '16px',
  lg: '18px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '28px',
  '4xl': '30px',
  '5xl': '36px',
  '6xl': '72px'
};

var DEFAULT_FONT_WEIGHTS = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700
};

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12;
var DEFAULT_PREDEFINED_CSS = {
  caption1: css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.text;
  }, function (p) {
    return p.theme.fontWeights.regular;
  }, function (p) {
    return p.theme.fontSizes.xs;
  }),
  body2: css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.text;
  }, function (p) {
    return p.theme.fontWeights.regular;
  }, function (p) {
    return p.theme.fontSizes.sm;
  }),
  body1: css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.text;
  }, function (p) {
    return p.theme.fontWeights.regular;
  }, function (p) {
    return p.theme.fontSizes.md;
  }),
  subTitle3: css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.text;
  }, function (p) {
    return p.theme.fontWeights.semiBold;
  }, function (p) {
    return p.theme.fontSizes.sm;
  }),
  subTitle2: css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.text;
  }, function (p) {
    return p.theme.fontWeights.bold;
  }, function (p) {
    return p.theme.fontSizes.md;
  }),
  subTitle1: css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.text;
  }, function (p) {
    return p.theme.fontWeights.bold;
  }, function (p) {
    return p.theme.fontSizes.lg;
  }),
  h6: css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.heading;
  }, function (p) {
    return p.theme.fontWeights.medium;
  }, function (p) {
    return p.theme.fontSizes.md;
  }),
  h5: css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.heading;
  }, function (p) {
    return p.theme.fontWeights.medium;
  }, function (p) {
    return p.theme.fontSizes.lg;
  }),
  h4: css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.heading;
  }, function (p) {
    return p.theme.fontWeights.medium;
  }, function (p) {
    return p.theme.fontSizes.xl;
  }),
  h3: css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    line-height: 150%;\n    font-family: ", ";\n    font-weight: ", ";\n    font-size: ", ";\n  "])), function (p) {
    return p.theme.fonts.heading;
  }, function (p) {
    return p.theme.fontWeights.medium;
  }, function (p) {
    return p.theme.fontSizes['2xl'];
  }),
  buttonReset: css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    appearance: none;\n    padding: 0;\n    margin: 0;\n    border: 0;\n    font: inherit;\n    color: inherit;\n    background-color: transparent;\n    cursor: pointer;\n    user-select: none;\n    overflow: visible;\n\n    &::-moz-focus-inner {\n      border: 0;\n      padding: 0;\n    }\n  "]))),
  borderBoxChildren: css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    -webkit-box-sizing: border-box;\n    box-sizing: border-box;\n    & *,\n    & *::before,\n    & *::after {\n      -webkit-box-sizing: border-box;\n      box-sizing: border-box;\n    }\n  "])))
};

var DEFAULT_THEME = {
  palette: DEFAULT_PALETTE,
  fonts: DEFAULT_FONTS,
  fontSizes: DEFAULT_FONT_SIZES,
  fontWeights: DEFAULT_FONT_WEIGHTS,
  predefinedCss: DEFAULT_PREDEFINED_CSS
};

var FlexiflowContext = /*#__PURE__*/React.createContext({
  theme: DEFAULT_THEME
});

function useFlexiflowContext() {
  return useContext(FlexiflowContext);
}

function FlexiflowComponent(_ref) {
  var children = _ref.children;

  var _useFlexiflowContext = useFlexiflowContext(),
      theme = _useFlexiflowContext.theme;

  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, children);
}

var CONFIG = {
  theme: DEFAULT_THEME
};
function FlexiflowProvider(_ref) {
  var children = _ref.children,
      _ref$params = _ref.params,
      params = _ref$params === void 0 ? {} : _ref$params;
  var config = useMemo(function () {
    return {
      theme: merge(CONFIG.theme, params)
    };
  }, [params]);
  return /*#__PURE__*/React.createElement(FlexiflowContext.Provider, {
    value: config
  }, children);
}

export { FlexiflowComponent, FlexiflowProvider, useFlexiflowContext };
